import useEmblaCarousel from 'embla-carousel-react'
import { FC, useId, useState } from 'react'

import { Carousel, Link, Image, IconButton } from '@lounge-fe/ui-kit'

import { OptionCarouselProps } from './types'
import classNames from 'classnames'
import { ArrowLeft, ArrowRight } from './Arrow'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'

export const OptionCarousel = <
  T extends { name: string; id: number | string },
>({
  selectedId,
  options,
  renderTitle,
  renderOption,
}: OptionCarouselProps<T>) => {
  const titleId = useId()

  const [emblaRef, embla] = useEmblaCarousel(
    {
      align: 'start',
      slidesToScroll: 1,
      dragFree: true,
      containScroll: 'trimSnaps',
      active: options.length > 3,
    },
    [WheelGesturesPlugin()],
  )

  const [pointerDown, setPointerDown] = useState(false)
  const [focusedId, setFocusedId] = useState<number | string | null>(null)
  const [hoveredId, setHoveredId] = useState<number | string | null>(null)

  const currentId = hoveredId ?? focusedId ?? selectedId
  const currentName = options.find((option) => option.id === currentId)?.name

  return (
    <div className="relative">
      {renderTitle && (
        <div id={titleId}>
          {renderTitle({
            currentOption: currentName,
          })}
        </div>
      )}

      <Carousel
        emblaRef={emblaRef}
        embla={embla}
        onMouseLeave={() => {
          setPointerDown(false)
          setHoveredId(null)
        }}
      >
        <ArrowLeft />
        <Carousel.Viewport aria-describedby={titleId}>
          <Carousel.Container className="gap-x-1 auto-cols-[86px] mt-2 group">
            {options.map((option, idx) => (
              <Carousel.Slide
                key={idx}
                index={idx}
                data-name={name}
                className={classNames(
                  'bg-surface-primary-default transition-colors border hover:!border-black inline-flex aspect-[5/7]',
                  currentId === option.id
                    ? 'border-black group-hover:border-white'
                    : 'border-white',
                )}
                onMouseLeave={() => setHoveredId(null)}
                onMouseEnter={() => !pointerDown && setHoveredId(option.id)}
                onBlur={() => setFocusedId(null)}
                onFocus={() => setFocusedId(option.id)}
                onMouseDown={() => setPointerDown(true)}
                onMouseUp={() => setPointerDown(false)}
                data-color={name}
              >
                {renderOption && renderOption(option)}
              </Carousel.Slide>
            ))}
          </Carousel.Container>
        </Carousel.Viewport>
        <ArrowRight />
      </Carousel>
    </div>
  )
}
